import { useCallback, useEffect, useMemo, useState } from "react";

import type { IOrdersEdges, IOrdersEdgesNode } from "../interface/customer";
import useCustomerQuery from "../queries/useCustomerQuery";
import type { IUseOrdersAmountProps } from "../types/hooks";
import { OrderFinancialStatus, OrderSortKeys } from "../types/storefront.types";

const useOrdersAmount = ({ accessToken, startDate, endDate, skip = false, lang = "ko", openToast = () => {} }: IUseOrdersAmountProps) => {
  const [allOrders, setAllOrders] = useState<IOrdersEdges["edges"] | []>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { fetchTotalPaidAmount } = useCustomerQuery({ lang, accessToken, update: () => {}, openToast });

  // 모든 주문을 한 번에 가져오는 함수
  const getAllOrders = useCallback(async () => {
    try {
      setIsLoading(true);

      let hasMorePages = true;
      let paginationCursor = null;
      let allCollectedOrders: IOrdersEdges["edges"] = [];

      // 첫 요청은 최대 250개 가져오기
      const first = 250;

      while (hasMorePages) {
        // eslint-disable-next-line no-await-in-loop
        const orderData = await fetchTotalPaidAmount({
          customerAccessToken: accessToken,
          first,
          after: paginationCursor,
          sortKey: OrderSortKeys.ProcessedAt,
          reverse: true,
          // query: `processed_at:>=2024-04-01 AND processed_at:<=2024-12-23`,
        });

        if (!orderData?.customer.orders) {
          break;
        }

        const currentOrders = orderData.customer.orders.edges || [];

        allCollectedOrders = [...allCollectedOrders, ...currentOrders];

        // 다음 페이지 확인
        hasMorePages = orderData.customer.orders.pageInfo.hasNextPage;
        paginationCursor = orderData.customer.orders.pageInfo.endCursor;
      }

      setAllOrders(allCollectedOrders);
    } catch (error) {
      throw new Error(`전체 주문 데이터 가져오기 실패: ${JSON.stringify(error)}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (skip) return;

    void getAllOrders();
  }, [skip]);

  // 기간내 실 주문금액 계산
  const totalOrderAmount = useMemo(() => {
    if (!allOrders.length) return 0;

    return allOrders
      .filter((edge: { node: IOrdersEdgesNode }) => {
        const processedAtDate = new Date(edge.node.processedAt);
        return (
          processedAtDate >= startDate &&
          processedAtDate < endDate &&
          edge.node.financialStatus !== OrderFinancialStatus.Pending &&
          edge.node.financialStatus !== OrderFinancialStatus.Voided &&
          edge.node.isfamilysale?.value !== "true"
        );
      })
      .reduce((total, item) => {
        const subTotalPrice = parseFloat(item.node.subtotalPrice?.amount ?? "0");
        const totalRefunded = parseFloat(item.node.totalRefunded.amount);
        const calculatedAmount = Math.round((subTotalPrice - totalRefunded) * 100) / 100;
        const accumulatedAmount = Math.max(Math.round(calculatedAmount * 100) / 100, 0);

        return total + accumulatedAmount;
      }, 0);
  }, [allOrders, startDate, endDate]);

  return {
    getAllOrders,
    totalOrderAmount,
    isOrdersLoading: isLoading,
  };
};

export default useOrdersAmount;
